import {
    EuiAccordion,
    EuiButtonIcon,
    EuiCheckbox,
    EuiFieldNumber,
    EuiFlexGroup,
    EuiFlexItem,
    EuiIcon,
    EuiPanel,
    EuiSpacer,
    EuiText,
    EuiTitle,
} from "@elastic/eui";
import React, { useCallback, useEffect, useState } from "react";

import { CategoryAdvancedOptions, CategoryScreen, Screen, ScreenOrientation } from "../../../utils/types";

const ColumnInfoContent = () => (
    <div style={{ fontSize: "0.7rem", color: "grey", marginLeft: "1rem" }}>
        <p>
            *For pricelists with two columns: <b>0</b> - Left column, <b>1</b> - Right column{" "}
        </p>
    </div>
);

const advancedOptionsList: { key: keyof CategoryAdvancedOptions; label: string }[] = [
    { key: "show_chis", label: "Show CHIS badges" },
    { key: "show_flags", label: "Show flags" },
    { key: "show_images", label: "Show images" },
    { key: "show_product_description", label: "Show product description" },
    { key: "show_strains", label: "Show strains" },
    { key: "use_custom_color", label: "Use custom color" },
    { key: "sort_prices_descending", label: "Sort prices descending" },
    { key: "include_joints_in_sorting", label: "Include joints in sorting by price" },
    { key: "enable_image_for_category", label: "Enable image for category" },
    { key: "color_opacity", label: "Custom Color Opacity" },
];

const defaultEnabledScreenAdvancedOptions = new Set<keyof CategoryAdvancedOptions>([
    "show_chis",
    "enable_image_for_category",
]);

export const ScreenListItemEditor = ({
    screen,
    handleDelete,
    handleOrderChange,
    handleColumnChange,
    handleAdvancedOptionsChange,
    screenOptions,
}: {
    screen: CategoryScreen;
    handleDelete: (id: string) => void;
    handleOrderChange: (id: string, newOrder: number) => void;
    handleColumnChange: (id: string, newColumn: number) => void;
    handleAdvancedOptionsChange: (id: string, options: CategoryAdvancedOptions) => void;
    screenOptions: Screen[];
}) => {
    const screenFromDb = screenOptions.find((opt) => opt.id === screen.screen_id) ?? {
        name: "-",
        id: screen.screen_id,
        icon: "desktop",
        orientation: ScreenOrientation.LANDSCAPE,
    };

    const getInitialAdvancedOptions = useCallback(() => {
        const initial: CategoryAdvancedOptions = {};
        for (const { key } of advancedOptionsList) {
            initial[key] = screen.advanced_options?.[key] ?? defaultEnabledScreenAdvancedOptions.has(key);
        }
        return initial;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [localOrder, setLocalOrder] = useState(screen.order_number);
    const [localColumn, setLocalColumn] = useState(screen.column_number);
    const [localAdvancedOptions, setLocalAdvancedOptions] = useState<CategoryAdvancedOptions | undefined>();

    useEffect(() => {
        if (!localAdvancedOptions) {
            const initialOptions = getInitialAdvancedOptions();
            handleAdvancedOptionsChange(screen.screen_id, initialOptions);
            setLocalAdvancedOptions(initialOptions);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [localAdvancedOptions]);

    useEffect(() => {
        setLocalOrder(screen.order_number);
        setLocalColumn(screen.column_number);
        setLocalAdvancedOptions(screen.advanced_options);
    }, [screen.order_number, screen.column_number, screen.advanced_options, getInitialAdvancedOptions]);

    if (!screenFromDb || !localAdvancedOptions) return null;

    const onOrderBlur = () => {
        if (localOrder !== screen.order_number) {
            handleOrderChange(screen.screen_id, localOrder);
        }
    };

    const onColumnBlur = () => {
        if (localColumn !== screen.column_number) {
            handleColumnChange(screen.screen_id, localColumn > 3 ? 3 : localColumn);
        }
    };

    function onAdvancedOptionChange(key: keyof CategoryAdvancedOptions, value: boolean | number) {
        const next = { ...localAdvancedOptions, [key]: value };
        setLocalAdvancedOptions(next);
        handleAdvancedOptionsChange(screen.screen_id, next);
    }

    const handlePercentageChange = (e: React.ChangeEvent<HTMLInputElement>, key: keyof CategoryAdvancedOptions) => {
        let number = Number(e.target.value);
        if (number > 100) {
            number = 100;
        } else if (number < 0) {
            number = 0;
        }
        onAdvancedOptionChange(key, Math.round(number) / 100);
    };

    const handleColumnInputChange = (e: React.ChangeEvent<HTMLInputElement>, screenId: string) => {
        let num = Number(e.target.value);

        if (num > 5) {
            num = 5;
        }

        setLocalColumn(num);
        handleColumnChange(screenId, num);
    };

    return (
        <EuiPanel>
            <EuiFlexGroup alignItems="center" justifyContent="spaceBetween">
                <EuiFlexItem>
                    <EuiFlexGroup gutterSize="s" alignItems="center">
                        <EuiFlexItem grow={false}>
                            <EuiIcon type="desktop" />
                        </EuiFlexItem>
                        <EuiFlexItem grow={false}>
                            <EuiTitle size="xs">
                                <h1>{screenFromDb?.name ?? ""}</h1>
                            </EuiTitle>
                        </EuiFlexItem>
                    </EuiFlexGroup>
                    <EuiSpacer size="s" />
                    <EuiFieldNumber
                        placeholder="Enter column number..."
                        step={1}
                        min={0}
                        max={5}
                        prepend={<EuiText>Column in Pricelist*</EuiText>}
                        value={localColumn}
                        onChange={(e) => handleColumnInputChange(e, screen.screen_id)}
                        onBlur={onColumnBlur}
                    />
                    <ColumnInfoContent />
                    <EuiSpacer size="s" />
                    <EuiFieldNumber
                        placeholder="Enter order number..."
                        step={1}
                        prepend={<EuiText>Order Number</EuiText>}
                        value={localOrder}
                        onChange={(e) => {
                            setLocalOrder(Number(e.target.value));
                            handleOrderChange(screen.screen_id, Number(e.target.value));
                        }}
                        onBlur={onOrderBlur}
                    />
                    <EuiSpacer size={"s"} />
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                    <EuiButtonIcon
                        color="danger"
                        aria-label="delete"
                        iconType="trash"
                        onClick={() => handleDelete(screenFromDb.id)}
                    />
                </EuiFlexItem>
            </EuiFlexGroup>
            <EuiAccordion
                id={`screen-adv-options-${screen.screen_id}`}
                buttonContent={<b>Display options</b>}
                arrowDisplay="right"
                paddingSize="s"
                initialIsOpen={true}
            >
                <EuiFlexGroup wrap gutterSize="s" css={{ width: "500px" }}>
                    {advancedOptionsList.map((option) => {
                        if (option.key.includes("opacity")) {
                            return (
                                <EuiFlexItem key={option.key} grow={false} css={{ width: "40%" }}>
                                    <EuiFlexGroup alignItems="center" gutterSize="xs">
                                        <EuiFlexItem>{option.label}:</EuiFlexItem>
                                        <EuiFlexItem>
                                            <EuiFieldNumber
                                                step={1}
                                                min={0}
                                                max={100}
                                                value={Number(localAdvancedOptions[option.key] ?? "0") * 100}
                                                onChange={(e) => {
                                                    handlePercentageChange(e, option.key);
                                                }}
                                            />
                                        </EuiFlexItem>
                                        %
                                    </EuiFlexGroup>
                                </EuiFlexItem>
                            );
                        }
                        return (
                            <EuiFlexItem key={option.key} grow={false} css={{ width: "40%" }}>
                                <EuiCheckbox
                                    id={`${option.key}-${screen.screen_id}`}
                                    label={option.label}
                                    checked={!!localAdvancedOptions[option.key]}
                                    onChange={(e) => onAdvancedOptionChange(option.key, e.target.checked)}
                                />
                            </EuiFlexItem>
                        );
                    })}
                </EuiFlexGroup>
            </EuiAccordion>
        </EuiPanel>
    );
};
